import type { ReactNode } from 'react';
import { Form, FormGroup, FormSection, TextInput } from '@ksyos/design-system';
import { useState } from 'react';
import { serviceOrganizationSettingAddEditSchema } from '@common/validation/serviceOrganizationSetting';
import { updateServiceOrganizationSetting } from '@frontend/services/api/serviceOrganizationSettingApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useServiceOrganizationSettingContext } from '@frontend/utils/hooks/useServiceOrganizationsSettingsContext';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import { ServiceOrganizationSettingFormMode } from '../../types';
import { ServiceOrganizationSettingButtons } from '../ServiceOrganizationSettingButtons';

type ServiceOrganizationSettingZorgmailEditProps = {
    handleClose: () => Promise<void>;
};

export function ServiceOrganizationSettingZorgmailEdit(props: ServiceOrganizationSettingZorgmailEditProps): ReactNode {
    const abortSignal = useAbortSignal();
    const { reloadServiceOrganizationSettings, serviceOrganizationSetting } = useServiceOrganizationSettingContext();
    const [errors, setErrors] = useState(emptyValidationErrors);
    const [isSaving, setIsSaving] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [zorgmailNumber, setZorgmailNumber] = useState<string | null>(serviceOrganizationSetting.zorgmailNummer);

    return (
        <>
            <Form submitOnEnter={false}>
                <FormSection>
                    <FormGroup
                        hasErrors={showErrors && errors.has('zorgmailNummer')}
                        label={getSelectedLanguage().serviceOrganizationSetting.zorgMailNumber}
                    >
                        <TextInput
                            error={showErrors ? getValidationErrorTranslation(errors.get('zorgmailNummer')) : undefined}
                            onChange={(value) => handleChange(value.target.value)}
                            value={zorgmailNumber ?? ''}
                        />
                    </FormGroup>
                </FormSection>
            </Form>
            <ServiceOrganizationSettingButtons
                isSaving={isSaving}
                mode={ServiceOrganizationSettingFormMode.edit}
                onClose={props.handleClose}
                onSave={() => handleSubmit(zorgmailNumber)}
            />
        </>
    );

    function handleChange(value: string): void {
        setZorgmailNumber(value || null);
        validateOne(value || null);
    }

    function validateOne(value: string | null): void {
        setErrors(
            updateValidationErrorsForOne(
                serviceOrganizationSettingAddEditSchema,
                { ...serviceOrganizationSetting, zorgmailNummer: value },
                errors,
                'zorgmailNummer'
            )
        );
    }

    async function handleSubmit(newZorgmailNumber: string | null): Promise<void> {
        setIsSaving(true);

        const validationResult = validate(serviceOrganizationSettingAddEditSchema, {
            ...serviceOrganizationSetting,
            zorgmailNummer: newZorgmailNumber,
        });

        if (validationResult.errors.size > 0 || !validationResult.item) {
            setShowErrors(true);
            setErrors(validationResult.errors);
            setIsSaving(false);

            return;
        }

        await updateServiceOrganizationSetting(serviceOrganizationSetting.id, validationResult.item, abortSignal);

        await reloadServiceOrganizationSettings();
        setIsSaving(false);
        await props.handleClose();
    }
}
